/**
 * HealthBar — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.healthBar,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { HEALTH_MAX, HUD_BAR_HEIGHT, HUD_BAR_ICON_SCALE } from './constants.js';

/**
 * Create health bar elements
 */
export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);

  // Health icon (+ symbol)
  const icon = new PIXI.Graphics();
  icon.setStrokeStyle({ width: 3, color: 0xffffff });
  icon.moveTo(-8, 0);
  icon.lineTo(8, 0);
  icon.moveTo(0, -8);
  icon.lineTo(0, 8);
  icon.stroke();
  container.addChild(icon);

  // Bar border
  const border = new PIXI.Graphics();
  container.addChild(border);

  // Bar background
  const background = new PIXI.Graphics();
  container.addChild(background);

  // Bar fill - use Sprite instead of Graphics for efficient scaling
  // This avoids clear()/redraw() every frame which causes WebGL state changes
  const fill = new PIXI.Sprite(PIXI.Texture.WHITE);
  fill.anchor.set(0, 0); // Scale from left edge
  fill.tint = 0x00ff00; // Default green
  container.addChild(fill);

  // Health text
  const text = new PIXI.Text({
    text: '100',
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'bold',
      fill: 0xffffff
    }
  });
  text.anchor.set(0, 0.5);
  container.addChild(text);

  hud.elements.healthBar = { container, icon, border, background, fill, text };
  hud._positionHealthBar();
  hud._createShieldBar();
  hud._createDriftBar();
}

/**
 * Position health bar
 */
export function position(hud) {
  const hb = hud.elements.healthBar;
  if (!hb) return;

  const marginLeft = 30;
  const marginBottom = 30;
  const barX = marginLeft + 50;
  const barY = hud.screenHeight - marginBottom - 30;
  const barWidth = 200;
  const barHeight = HUD_BAR_HEIGHT;

  hb.container.x = 0;
  hb.container.y = 0;

  // Icon position
  hb.icon.scale.set(HUD_BAR_ICON_SCALE);
  hb.icon.x = marginLeft + 15;
  hb.icon.y = barY + barHeight / 2;

  // Draw border
  hb.border.clear();
  hb.border.setStrokeStyle({ width: 2, color: 0xffffff });
  hb.border.rect(barX, barY, barWidth, barHeight);
  hb.border.stroke();

  // Draw background
  hb.background.clear();
  hb.background.rect(barX + 2, barY + 2, barWidth - 4, barHeight - 4);
  hb.background.fill({ color: 0x222222 });

  // Position and size the fill sprite (Sprite instead of Graphics for performance)
  hb.fill.x = barX + 2;
  hb.fill.y = barY + 2;
  hb.fill.width = barWidth - 4; // Full width, will be scaled by scale.x
  hb.fill.height = barHeight - 4;

  // Text position
  hb.text.x = barX + barWidth + 15;
  hb.text.y = barY + barHeight / 2;

  // Store dimensions for updates
  hb.barX = barX;
  hb.barY = barY;
  hb.barWidth = barWidth;
  hb.barHeight = barHeight;
  hb.fillMaxWidth = barWidth - 4; // Store max width for scaling
}

/**
 * Update health bar
 * @param {Object} localPlayer - Local player data
 */
export function update(hud, localPlayer) {
  const hb = hud.elements.healthBar;
  if (!hb || !localPlayer?.health) return;

  const health = localPlayer.health;
  const currentHealth = health.currentHealth || 0;
  const maxHealth = health.maxHealth || HEALTH_MAX;
  const healthPercent = Math.max(0, Math.min(1, currentHealth / maxHealth));

  // Update fill using width instead of clear/redraw (much more efficient)
  const barColor = currentHealth <= 30 ? 0xff0000 : 0x00ff00;
  hb.fill.tint = barColor;
  hb.fill.width = hb.fillMaxWidth * healthPercent;

  hud.updateShieldBar(localPlayer);

  // Update text
  hb.text.text = Math.round(currentHealth).toString();

  // Update player name on shield bar (if it exists and hasn't been set yet)
  const sb = hud.elements.shieldBar;
  if (sb && sb.nameText && localPlayer.name && sb.nameText.text !== localPlayer.name) {
    sb.nameText.text = localPlayer.name;
  }
}
